import React, { useEffect, useState, useRef } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Av, Spinner, Input, Badge, Tog, Modal, Field, Textarea, Select } from '../ui.jsx';

export function Messaging({ toast }) {
  const [convos, setConvos] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [showPlaybook, setShowPlaybook] = useState(false);
  const [view, setView] = useState('sms'); // 'sms' | 'all' | 'call' | 'email' | 'facebook_messenger' | 'instagram_dm'
  const [allComms, setAllComms] = useState([]);
  // GHL-style: clicking ANY non-SMS row populates the right panel with the
  // full thread for that contact (or address if no contact link). SMS view
  // still uses the conversation-based path so we keep AI toggle + reply
  // continuity for existing SMS conversations.
  const [activeContactId, setActiveContactId] = useState(null);
  const [activeAddr, setActiveAddr] = useState(null); // phone/email fallback when no contact link
  const [contactThread, setContactThread] = useState(null);
  useEffect(() => {
    if (view === 'sms') return;
    const url = view === 'all' ? '/api/messaging/all-comms'
                                : `/api/messaging/all-comms?channel=${view}`;
    api(url).then(setAllComms).catch(()=>setAllComms([]));
  }, [view]);
  // Load unified contact thread (cross-channel) when a contact is selected.
  useEffect(() => {
    if (!activeContactId) return;
    api(`/api/messaging/contacts/${activeContactId}/thread`).then(setContactThread).catch(()=>setContactThread(null));
  }, [activeContactId]);
  // No-contact fallback: build a synthetic thread from allComms filtered by
  // matching from/to address. Useful for inbound calls/emails from unknown
  // numbers — the owner can still see history and reach out.
  useEffect(() => {
    if (!activeAddr) return;
    const matching = allComms.filter(c =>
      (c.fromAddr && c.fromAddr === activeAddr) ||
      (c.toAddr && c.toAddr === activeAddr)
    );
    const isEmail = activeAddr.includes('@');
    setContactThread({
      contact: { id: null, name: activeAddr, phone: isEmail ? null : activeAddr, email: isEmail ? activeAddr : null },
      conversation: null,
      items: matching.map(c => ({
        id: c.id, channel: c.channel, direction: c.direction,
        subject: c.subject, body: c.body,
        fromAddr: c.fromAddr, toAddr: c.toAddr,
        durationSeconds: c.durationSeconds, status: c.status,
        sentAt: c.createdAt,
      })).sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt)),
    });
  }, [activeAddr, allComms]);
  // Switching views resets the active selection so the right panel doesn't
  // show stale state from the previous tab.
  useEffect(() => {
    if (view === 'sms') { setActiveContactId(null); setActiveAddr(null); setContactThread(null); }
    else { setActiveId(null); setThread(null); }
  }, [view]);
  const openContact = (id) => { if (id) window.dispatchEvent(new CustomEvent('tradeops:open-contact', { detail: { contactId: id }})); };
  const [playbook, setPlaybook] = useState(null);
  const messagesScrollRef = useRef(null);
  const lastMsgCountRef = useRef(0);

  useEffect(() => { api('/api/messaging/playbook').then(setPlaybook).catch(()=>{}); }, []);

  const toggleAi = async () => {
    if (!thread) return;
    const newState = !thread.conversation.aiActive;
    try {
      await api(`/api/messaging/conversations/${activeId}/ai`, { method:'POST', body:{ enabled: newState }});
      const updated = await api(`/api/messaging/conversations/${activeId}/messages`);
      setThread(updated);
      toast(newState ? '🤖 AI taking over this conversation' : '👤 Handed back to your team');
    } catch(e) { toast(e.message,'error'); }
  };

  const loadConvos = () => api('/api/messaging/conversations').then(c => {
    setConvos(c);
    if (!activeId && c[0]) setActiveId(c[0].id);
  }).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(() => { loadConvos(); const i = setInterval(loadConvos, 15000); return ()=>clearInterval(i); }, []);

  useEffect(() => {
    if (!activeId) return;
    api(`/api/messaging/conversations/${activeId}/messages`).then(setThread).catch(()=>{});
  }, [activeId]);

  // Only auto-scroll the inner messages container, never the page.
  // Trigger only when (a) conversation changes, or (b) message count increases.
  useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el || !thread) return;
    const newCount = thread.messages?.length || 0;
    const prevCount = lastMsgCountRef.current;
    lastMsgCountRef.current = newCount;
    if (newCount > prevCount || newCount === prevCount) {
      // Scroll inner container only
      el.scrollTop = el.scrollHeight;
    }
  }, [thread, activeId]);
  // Reset count tracker when switching conversations
  useEffect(() => { lastMsgCountRef.current = 0; }, [activeId]);

  // Channel toggle — reply via the same channel the thread is on, OR force
  // email/SMS so the owner can switch mid-conversation.
  const [sendChannel, setSendChannel] = useState('auto');
  const [emailSubject, setEmailSubject] = useState('');
  const send = async () => {
    if (!draft.trim()) return;
    const c = thread?.conversation || {};
    const effectiveChannel = sendChannel === 'auto' ? (c.channel || 'sms') : sendChannel;
    if (effectiveChannel === 'email' && !c.contactEmail && !c.contactId) {
      toast('No email on this contact — pick SMS or add an email to the contact first', 'error');
      return;
    }
    if (effectiveChannel === 'sms' && !c.contactPhone) {
      toast('No phone on this contact — pick Email instead', 'error');
      return;
    }
    setSending(true);
    try {
      await api('/api/messaging/send', { method:'POST', body:{
        conversationId: c.id,
        phone: c.contactPhone,
        email: c.contactEmail,
        contactId: c.contactId,
        body: draft,
        subject: effectiveChannel === 'email' ? (emailSubject || 'Re: your inquiry') : undefined,
        channel: sendChannel === 'auto' ? undefined : sendChannel,
      }});
      setDraft('');
      setEmailSubject('');
      const updated = await api(`/api/messaging/conversations/${activeId}/messages`);
      setThread(updated);
      loadConvos();
    } catch(e) { toast(e.message,'error'); }
    finally { setSending(false); }
  };

  // Use 100% of the parent (the screen-wrapper div in App.jsx) instead of
  // 100vh − 64px. The new SubTabBar adds another header row, so the fixed
  // viewport math drifted and pushed the right-panel scroll past the bottom
  // of the screen — the page scrolled instead of the message list.
  return <div style={{ display:'grid', gridTemplateColumns:'320px 1fr', height:'100%', minHeight:'600px' }}>
    <div style={{ borderRight:`1px solid ${G.border}`, overflow:'auto' }}>
      <div style={{ padding:'18px 18px 10px 18px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <h2 style={{ margin:0, fontSize:18 }}>Messages</h2>
        <Btn sz="xs" v={playbook?.enabled?'green':'ghost'} onClick={()=>setShowPlaybook(true)}>🤖 AI {playbook?.enabled?'On':'Off'}</Btn>
      </div>
      <div style={{ padding:'0 18px 8px 18px', display:'flex', gap:6, flexWrap:'wrap' }}>
        {[
          ['sms','💬 SMS'],
          ['facebook_messenger','💬 FB DM'],
          ['instagram_dm','📱 IG DM'],
          ['call','📞 Calls'],
          ['email','📧 Email'],
          ['all','📋 All'],
        ].map(([k,l]) => (
          <Btn key={k} sz="xs" v={view===k?'primary':'ghost'} onClick={()=>setView(k)}>{l}</Btn>
        ))}
      </div>
      {loading ? <div style={{ padding:24 }}><Spinner/></div>
      : view !== 'sms' ? (
        allComms.length === 0
          ? <div style={{ padding:24, color:G.textMuted, fontSize:13, textAlign:'center' }}>
              No {view === 'all' ? 'communications' : view + 's'} yet.
            </div>
          : (() => {
              // Collapse the feed by contact (when linked) OR by phone/email
              // address (fallback). Every row clicks through.
              const byKey = new Map();
              for (const c of allComms) {
                // Prefer the address most likely to identify the other party.
                const addr = c.direction === 'inbound' ? (c.fromAddr || c.toAddr) : (c.toAddr || c.fromAddr);
                const key = c.contact?.id ? `c:${c.contact.id}` : `a:${addr || c.id}`;
                if (!byKey.has(key)) {
                  byKey.set(key, {
                    key,
                    contactId: c.contact?.id || null,
                    addr: addr || null,
                    name: c.contact?.name || addr || 'Unknown',
                    latest: c, count: 0,
                  });
                }
                byKey.get(key).count++;
              }
              return Array.from(byKey.values()).map(row => {
                const c = row.latest;
                const icon = c.channel === 'sms' ? '💬' : c.channel === 'call' ? '📞' : c.channel === 'email' ? '📧' : c.channel === 'facebook_messenger' ? '💬' : c.channel === 'instagram_dm' ? '📱' : '🔌';
                const dirLabel = c.direction === 'inbound' ? '←' : '→';
                const preview = c.channel === 'call'
                  ? (c.body ? `${dirLabel} Call · ${Math.round((c.durationSeconds||0)/60)}min · ${c.body.slice(0,60)}` : `${dirLabel} Call · ${Math.round((c.durationSeconds||0)/60)}min`)
                  : `${dirLabel} ${(c.subject?c.subject+' · ':'')}${(c.body||'').slice(0,60)}`;
                const isActive = (row.contactId && activeContactId === row.contactId)
                              || (!row.contactId && activeAddr === row.addr);
                const pick = () => {
                  if (row.contactId) {
                    setActiveAddr(null);
                    setContactThread(null); // clear stale fallback thread
                    setActiveContactId(row.contactId);
                  } else if (row.addr) {
                    setActiveContactId(null);
                    setContactThread(null);
                    setActiveAddr(row.addr);
                  } else {
                    // Last resort: show this single comm.
                    setActiveContactId(null);
                    setActiveAddr(null);
                    setContactThread({
                      contact: { id: null, name: 'Unknown', phone: null, email: null },
                      conversation: null,
                      items: [{
                        id: c.id, channel: c.channel, direction: c.direction,
                        subject: c.subject, body: c.body, fromAddr: c.fromAddr, toAddr: c.toAddr,
                        durationSeconds: c.durationSeconds, status: c.status, sentAt: c.createdAt,
                      }],
                    });
                  }
                };
                return <div key={row.key}
                  onClick={pick}
                  style={{ padding:'12px 18px', display:'flex', alignItems:'flex-start', gap:10, borderBottom:`1px solid ${G.border}`, cursor:'pointer', borderLeft:`3px solid ${isActive?G.purple:'transparent'}`, background: isActive?G.purpleDim:'transparent' }}>
                  <div style={{ fontSize:18 }}>{icon}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
                      <strong style={{ fontSize:14, color: row.contactId ? G.cyan : G.text }}>
                        {row.name}
                        {row.count > 1 && <span style={{ marginLeft:6, fontSize:10, color:G.textMuted, fontWeight:500 }}>· {row.count}</span>}
                      </strong>
                      <span style={{ fontSize:11, color:G.textMuted }}>{new Date(c.createdAt).toLocaleString()}</span>
                    </div>
                    <div style={{ fontSize:12, color:G.textMuted, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{preview}</div>
                  </div>
                </div>;
              });
            })()
      )
      : convos.length === 0 ? <div style={{ padding:24, color:G.textMuted, fontSize:13, textAlign:'center' }}>No conversations yet. SMS to and from leads will show up here.</div>
      : convos.map(c => {
        const channelBadge = c.channel === 'facebook_messenger' ? '💬'
                           : c.channel === 'instagram_dm' ? '📱'
                           : c.channel === 'email' ? '📧'
                           : c.channel === 'voice' ? '📞'
                           : '';
        return <div key={c.id} onClick={()=>setActiveId(c.id)} style={{ padding:'12px 18px', display:'flex', alignItems:'center', gap:10, cursor:'pointer', borderLeft:`3px solid ${activeId===c.id?G.purple:'transparent'}`, background:activeId===c.id?G.purpleDim:'transparent' }}>
          <Av name={c.contactName||'?'} sz={36}/>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              {channelBadge && <span title={c.channel} style={{ fontSize:14 }}>{channelBadge}</span>}
              <strong
                onClick={(e)=>{ e.stopPropagation(); openContact(c.contactId); }}
                style={{ fontSize:14, cursor: c.contactId?'pointer':'default', color: c.contactId?G.cyan:G.text }}
                title={c.contactId ? 'Click to open profile' : ''}>
                {c.contactName||c.contactPhone}
              </strong>
              {c.unread > 0 && <span style={{ fontSize:10, background:G.red, color:'#fff', padding:'1px 6px', borderRadius:8, fontWeight:700 }}>{c.unread}</span>}
            </div>
            <div style={{ fontSize:12, color:G.textMuted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.lastMessage}</div>
          </div>
        </div>;
      })}
    </div>

    <div style={{ display:'flex', flexDirection:'column' }}>
      {/* Non-SMS view: unified per-contact (or per-address) thread */}
      {view !== 'sms' && contactThread ? (
        <ContactThreadPanel
          thread={contactThread}
          openContact={openContact}
          toast={toast}
          onReplied={() => {
            if (activeContactId) api(`/api/messaging/contacts/${activeContactId}/thread`).then(setContactThread).catch(()=>{});
            // address-keyed threads refresh when allComms refetches; no-op here.
          }}
        />
      ) : view !== 'sms' ? (
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:G.textMuted, padding:24, textAlign:'center' }}>
          Click any communication on the left to see the full history with that contact across every channel.
        </div>
      ) : !thread ? <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:G.textMuted }}>Select a conversation</div>
      : <>
        <div style={{ padding:'14px 22px', borderBottom:`1px solid ${G.border}`, display:'flex', alignItems:'center', gap:12 }}>
          <Av name={thread.conversation.contactName||'?'}/>
          <div style={{ flex:1 }}>
            <strong
              onClick={()=>openContact(thread.conversation.contactId)}
              style={{ cursor: thread.conversation.contactId?'pointer':'default', color: thread.conversation.contactId?G.cyan:G.text }}>
              {thread.conversation.contactName}
            </strong>
            <div style={{ fontSize:12, color:G.textMuted }}>{thread.conversation.contactPhone}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:12, color: thread.conversation.aiActive ? G.green : G.textMuted, fontWeight:600 }}>
              {thread.conversation.aiActive ? '🤖 AI Active' : '👤 Human'}
            </span>
            <Tog on={!!thread.conversation.aiActive} onChange={toggleAi}/>
          </div>
        </div>
        {thread.conversation.aiActive && <div style={{ padding:'8px 22px', background:G.greenLight, color:G.green, fontSize:12, fontWeight:600 }}>
          🤖 AI is responding to inbound messages using your playbook. Toggle off to take over.
        </div>}
        <div ref={messagesScrollRef} style={{ flex:1, overflow:'auto', padding:22, overscrollBehavior:'contain' }}>
          {thread.messages.map(m => (
            <div key={m.id} style={{ display:'flex', justifyContent:m.direction==='outbound'?'flex-end':'flex-start', marginBottom:8 }}>
              <div style={{ maxWidth:'70%', padding:'10px 14px', borderRadius:12, background: m.direction==='outbound'?G.purple+'33':G.input, border:`1px solid ${m.direction==='outbound'?G.purpleBorder:G.cardBorder}` }}>
                <div style={{ fontSize:14, lineHeight:1.4 }}>{m.body}</div>
                <div style={{ fontSize:10, color:G.textMuted, marginTop:4 }}>{new Date(m.sentAt).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
        {/* Channel switch + email subject (when in email mode) */}
        <div style={{ padding:'8px 14px', borderTop:`1px solid ${G.border}`, background:G.input, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <span style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>Reply via:</span>
          {[
            { v:'auto',  label:'Auto', sub:thread.conversation.channel || 'sms' },
            { v:'sms',   label:'💬 SMS',   sub:thread.conversation.contactPhone || '(no phone)' },
            { v:'email', label:'📧 Email', sub:thread.conversation.contactEmail || '(no email)' },
          ].map(opt => (
            <button key={opt.v} onClick={()=>setSendChannel(opt.v)} title={opt.sub} style={{
              padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer',
              border:`1px solid ${sendChannel===opt.v ? G.purple : G.cardBorder}`,
              background: sendChannel===opt.v ? G.purpleDim : 'transparent',
              color: sendChannel===opt.v ? G.purple : G.text,
            }}>{opt.label}</button>
          ))}
          {(sendChannel === 'email' || (sendChannel === 'auto' && thread.conversation.channel === 'email')) && (
            <input
              value={emailSubject}
              onChange={e=>setEmailSubject(e.target.value)}
              placeholder="Email subject…"
              style={{ flex:1, minWidth:160, ...styles.inp, padding:'6px 10px', fontSize:12 }}
            />
          )}
        </div>
        <ReplyBar
          draft={draft} setDraft={setDraft}
          send={send} sending={sending}
          toast={toast}
          contactName={thread.conversation.contactName || thread.conversation.contactPhone}
        />
      </>}
    </div>
    <PlaybookModal open={showPlaybook} initial={playbook} onClose={()=>setShowPlaybook(false)} onSaved={p=>{ setPlaybook(p); setShowPlaybook(false); toast('AI playbook saved'); }} toast={toast}/>
  </div>;
}

// ─── ContactThreadPanel ─────────────────────────────────────────────────────
// GHL-style unified panel. Shows every comm (SMS, email, call, DM) the
// account has had with a single contact in time order, with a reply bar
// that picks the channel based on what's available + most recently used.
function ContactThreadPanel({ thread, openContact, toast, onReplied }) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState('');
  // Pick reply channel: most-recent inbound channel → most-recent any → SMS fallback.
  const inferred = (() => {
    const inbound = [...(thread.items||[])].reverse().find(i => i.direction === 'inbound');
    const any = thread.items?.[thread.items.length-1];
    const c = inbound || any;
    return c?.channel === 'email' ? 'email' : 'sms';
  })();
  const [channel, setChannel] = useState(inferred);
  const c = thread.contact || {};
  const send = async () => {
    if (!draft.trim()) return;
    if (channel === 'sms' && !c.phone) { toast('No phone on file — pick Email instead', 'error'); return; }
    if (channel === 'email' && !c.email) { toast('No email on file — pick SMS instead', 'error'); return; }
    setSending(true);
    try {
      await api('/api/messaging/send', { method:'POST', body:{
        phone: c.phone, email: c.email, contactId: c.id,
        body: draft, subject: channel==='email' ? (subject || 'Re: your inquiry') : undefined,
        conversationId: thread.conversation?.id,
        channel,
      }});
      setDraft(''); setSubject('');
      onReplied && onReplied();
    } catch(e) { toast(e.message, 'error'); }
    finally { setSending(false); }
  };
  const scrollRef = useRef(null);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [thread]);
  return <>
    <div style={{ padding:'14px 22px', borderBottom:`1px solid ${G.border}`, display:'flex', alignItems:'center', gap:12 }}>
      <Av name={c.name||'?'}/>
      <div style={{ flex:1 }}>
        <strong
          onClick={()=>openContact(c.id)}
          style={{ cursor: c.id?'pointer':'default', color: c.id?G.cyan:G.text }}>
          {c.name || c.phone || c.email || 'Unknown'}
        </strong>
        <div style={{ fontSize:12, color:G.textMuted, display:'flex', gap:10 }}>
          {c.phone && <span>📞 {c.phone}</span>}
          {c.email && <span>✉ {c.email}</span>}
        </div>
      </div>
      {c.id && <Btn sz="xs" v="cyan" onClick={()=>openContact(c.id)}>Open contact</Btn>}
    </div>
    <div ref={scrollRef} style={{ flex:1, overflow:'auto', padding:22, overscrollBehavior:'contain' }}>
      {(thread.items||[]).length === 0 ? (
        <div style={{ color:G.textMuted, textAlign:'center', padding:40 }}>No history yet.</div>
      ) : (thread.items||[]).map(m => {
        const isOut = m.direction === 'outbound';
        const chanIcon = m.channel === 'call' ? '📞' : m.channel === 'email' ? '📧' : m.channel === 'facebook_messenger' ? '💬' : m.channel === 'instagram_dm' ? '📱' : '💬';
        return <div key={m.id} style={{ display:'flex', justifyContent: isOut?'flex-end':'flex-start', marginBottom:10 }}>
          <div style={{ maxWidth:'72%', padding:'10px 14px', borderRadius:12,
            background: isOut?G.purple+'33':G.input,
            border:`1px solid ${isOut?G.purpleBorder:G.cardBorder}` }}>
            <div style={{ fontSize:11, color:G.textMuted, marginBottom:4, display:'flex', gap:6, alignItems:'center' }}>
              <span>{chanIcon}</span>
              <span style={{ textTransform:'uppercase', letterSpacing:'.05em', fontWeight:700 }}>{m.channel}</span>
              {m.channel === 'call' && m.durationSeconds ? <span>· {Math.round(m.durationSeconds/60)}min</span> : null}
              <span style={{ marginLeft:'auto' }}>{new Date(m.sentAt).toLocaleString()}</span>
            </div>
            {m.subject && <div style={{ fontSize:13, fontWeight:700, marginBottom:4 }}>{m.subject}</div>}
            <div style={{ fontSize:14, lineHeight:1.45, whiteSpace:'pre-wrap', wordBreak:'break-word' }}>
              {m.body || <em style={{ color:G.textMuted }}>(no content)</em>}
            </div>
          </div>
        </div>;
      })}
    </div>
    <div style={{ padding:'8px 14px', borderTop:`1px solid ${G.border}`, background:G.input, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
      <span style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>Reply via:</span>
      {[
        { v:'sms',   label:'💬 SMS',   sub:c.phone || '(no phone)' },
        { v:'email', label:'📧 Email', sub:c.email || '(no email)' },
      ].map(opt => (
        <button key={opt.v} onClick={()=>setChannel(opt.v)} title={opt.sub} style={{
          padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer',
          border:`1px solid ${channel===opt.v ? G.purple : G.cardBorder}`,
          background: channel===opt.v ? G.purpleDim : 'transparent',
          color: channel===opt.v ? G.purple : G.text,
        }}>{opt.label}</button>
      ))}
      {channel === 'email' && (
        <input
          value={subject}
          onChange={e=>setSubject(e.target.value)}
          placeholder="Email subject…"
          style={{ flex:1, minWidth:160, ...styles.inp, padding:'6px 10px', fontSize:12 }}
        />
      )}
    </div>
    <ReplyBar
      draft={draft} setDraft={setDraft}
      send={send} sending={sending}
      toast={toast}
      contactName={c.name || c.phone || c.email}
    />
  </>;
}

// ─── Reply bar: textarea + emoji + SMS templates + send ─────────────────────
const QUICK_EMOJIS = ['👍','✅','🙏','🔧','🚐','⏰','📅','💰','📍','📞','🎉','⚠️','❓','🙌','💬','✨'];

function ReplyBar({ draft, setDraft, send, sending, toast, contactName }) {
  const [showEmoji, setShowEmoji] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api('/api/messaging/templates').then(r => setTemplates(r.templates || [])).catch(()=>{});
  }, []);

  const insertEmoji = (e) => {
    setDraft(d => d + e);
    setShowEmoji(false);
  };

  const useTemplate = (t) => {
    // Interpolate {first_name} from contact name (first word)
    const firstName = (contactName || '').split(' ')[0] || 'there';
    const body = (t.body || '').replace(/\{first_name\}/g, firstName);
    setDraft(body);
    setShowTemplates(false);
  };

  const segs = Math.max(1, Math.ceil((draft||'').length / 153));
  return (
    <div style={{ padding:14, borderTop:`1px solid ${G.border}`, display:'flex', flexDirection:'column', gap:8, background:G.card, position:'relative' }}>
      {/* Quick action bar */}
      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
        <button
          onClick={()=>{ setShowEmoji(s=>!s); setShowTemplates(false); }}
          style={qBtn(showEmoji)}
          title="Insert emoji">😀</button>
        <button
          onClick={()=>{ setShowTemplates(s=>!s); setShowEmoji(false); }}
          style={qBtn(showTemplates)}
          title="Quick templates">📋 Templates</button>
        <div style={{ flex:1 }}/>
        <span style={{ fontSize:11, color:G.textMuted }}>
          {draft ? `${draft.length} chars · ${segs} SMS` : 'Enter to send · Shift+Enter for new line'}
        </span>
      </div>

      {/* Emoji panel */}
      {showEmoji && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:4, padding:8, background:G.input, borderRadius:8 }}>
          {QUICK_EMOJIS.map(e => (
            <button key={e} onClick={()=>insertEmoji(e)} style={{
              background:'none', border:'none', padding:'6px 8px', cursor:'pointer',
              fontSize:22, borderRadius:6, transition:'.1s',
            }} onMouseEnter={e=>e.target.style.background=G.cardBorder} onMouseLeave={e=>e.target.style.background='none'}>{e}</button>
          ))}
        </div>
      )}

      {/* Templates panel */}
      {showTemplates && (
        <div style={{ padding:8, background:G.input, borderRadius:8, maxHeight:260, overflowY:'auto' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, padding:'0 4px' }}>
            <strong style={{ fontSize:12, color:G.textMuted }}>QUICK TEMPLATES</strong>
            <a href="#" onClick={(e)=>{ e.preventDefault(); window.dispatchEvent(new CustomEvent('tradeops:nav', { detail: { tab:'messaging' }})); window.dispatchEvent(new CustomEvent('tradeops:open-templates')); setShowTemplates(false); }}
              style={{ fontSize:11, color:G.cyan }}>Manage →</a>
          </div>
          {templates.length === 0 ? (
            <div style={{ padding:14, color:G.textMuted, fontSize:13, textAlign:'center' }}>No templates yet. Click "Manage →" to add some.</div>
          ) : templates.map(t => (
            <div key={t.id} onClick={()=>useTemplate(t)} style={{
              padding:'10px 12px', borderRadius:6, cursor:'pointer', marginBottom:4,
              background:G.bg, transition:'.1s',
            }} onMouseEnter={e=>e.currentTarget.style.background=G.cardBorder} onMouseLeave={e=>e.currentTarget.style.background=G.bg}>
              <div style={{ fontSize:12, fontWeight:600, color:G.purple }}>{t.name}</div>
              <div style={{ fontSize:12, color:G.textMuted, marginTop:2, lineHeight:1.4 }}>{t.body}</div>
            </div>
          ))}
        </div>
      )}

      {/* Input + send */}
      <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
        <Textarea
          value={draft}
          onChange={e=>setDraft(e.target.value)}
          placeholder={`Reply to ${contactName||'this contact'}...`}
          style={{ minHeight:60, maxHeight:160, fontSize:14, resize:'vertical' }}
          onKeyDown={e=>{ if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); send(); }}}
        />
        <Btn v="primary" onClick={send} disabled={sending || !draft.trim()} style={{ minWidth:64, height:60 }}>
          {sending ? <Spinner sz={14}/> : '➤ Send'}
        </Btn>
      </div>
    </div>
  );
}

function qBtn(active) {
  return {
    background: active ? '#8b5cf6' : 'transparent',
    color: active ? '#fff' : '#a0a0b8',
    border: `1px solid ${active ? '#8b5cf6' : '#252535'}`,
    padding:'6px 10px', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer',
  };
}

function PlaybookModal({ open, initial, onClose, onSaved, toast }) {
  const [enabled, setEnabled] = useState(false);
  const [playbook, setPlaybook] = useState('');
  const [busy, setBusy] = useState(false);
  const [testInput, setTestInput] = useState('Hi, my AC stopped working last night and I have a kid at home. Can someone come today?');
  const [testReply, setTestReply] = useState(null);

  useEffect(() => { if (open && initial) { setEnabled(initial.enabled); setPlaybook(initial.playbook||''); } }, [open, initial]);
  if (!open) return null;

  const save = async () => {
    setBusy(true);
    try { const r = await api('/api/messaging/playbook', { method:'PATCH', body:{ enabled, playbook }});
      onSaved({ enabled, playbook, isDefault:false, anthropicConfigured: initial.anthropicConfigured });
    } catch(e) { toast(e.message,'error'); setBusy(false); }
  };

  const tryAi = async () => {
    setBusy(true); setTestReply(null);
    try {
      // Find any conversation to use as context — fallback to a /test endpoint with no convo
      const convos = await api('/api/messaging/conversations');
      if (!convos.length) { toast('No conversations to test against', 'warn'); setBusy(false); return; }
      const r = await api(`/api/messaging/conversations/${convos[0].id}/ai-test`, { method:'POST', body:{ incomingMessage: testInput }});
      setTestReply(r);
    } catch(e) { toast(e.message,'error'); }
    finally { setBusy(false); }
  };

  return <Modal open onClose={onClose} width={780}>
    <h2 style={{ margin:'0 0 4px 0' }}>🤖 AI Chat Playbook</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
      The AI uses this playbook to respond to inbound SMS when enabled per-conversation. Edit booking script, objection handling, escalation triggers, and identity.
      {!initial?.anthropicConfigured && <div style={{ marginTop:6, color:G.yellow, fontSize:12 }}>⚠️ ANTHROPIC_API_KEY not set — AI runs in mock mode (logged replies only).</div>}
    </div>

    <div style={{ ...styles.card, padding:14, marginBottom:14, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
      <div>
        <strong>Master AI Switch</strong>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>Per-conversation AI toggles only work when this is on.</div>
      </div>
      <Tog on={enabled} onChange={()=>setEnabled(!enabled)}/>
    </div>

    <Field label="Playbook">
      <Textarea value={playbook} onChange={e=>setPlaybook(e.target.value)} style={{ minHeight:280, fontFamily:'monospace', fontSize:12 }}/>
    </Field>

    <div style={{ ...styles.card, padding:14, background:G.input, marginBottom:14 }}>
      <strong style={{ fontSize:13 }}>🧪 Test the AI</strong>
      <div style={{ color:G.textMuted, fontSize:12, marginTop:4, marginBottom:8 }}>Pretend a customer texts this — see what the AI would reply.</div>
      <Textarea value={testInput} onChange={e=>setTestInput(e.target.value)} style={{ minHeight:60, marginBottom:8 }}/>
      <Btn sz="sm" onClick={tryAi} disabled={busy}>{busy?<Spinner/>:'Generate Reply'}</Btn>
      {testReply && <div style={{ marginTop:10, padding:12, background:G.purpleDim, borderRadius:8, fontSize:13, lineHeight:1.5 }}>
        <strong style={{ color:G.purple }}>AI:</strong> {testReply.text || '(no reply)'}
        {testReply.handoff && <div style={{ marginTop:6, color:G.yellow, fontSize:12 }}>⚠️ AI flagged for human handoff</div>}
        {testReply.skip && <div style={{ marginTop:6, color:G.textMuted, fontSize:12 }}>↳ AI chose to skip</div>}
      </div>}
    </div>

    <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save} disabled={busy}>{busy?<Spinner/>:'Save Playbook'}</Btn>
    </div>
  </Modal>;
}
